import React, { useState } from 'react';
import axios from 'axios';
import styles from './EmailDetailModal.module.css';
import { URL as BASEURL } from '../../AxiosUrlGlobal';

/**
 * EmailDetailModal - Display email details and reply functionality
 */
function EmailDetailModal({ email, onClose, userId, token, onDelete }) {
    const [showReply, setShowReply] = useState(false);
    const [replyData, setReplyData] = useState({
        from: email.accountEmail || '',
        to: email.from?.address || '',
        subject: email.subject?.startsWith('Re:') ? email.subject : `Re: ${email.subject}`,
        text: '',
        html: ''
    });
    const [sending, setSending] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const formatDate = (date) => {
        const d = new Date(date);
        return d.toLocaleString();
    };

    const getFromDisplay = (from) => {
        if (!from) return 'Unknown';
        if (from.name) {
            return `${from.name} <${from.address}>`;
        }
        return from.address || 'Unknown';
    };

    const getToDisplay = (to) => {
        if (!to || to.length === 0) return 'Unknown';
        return to.map(t => t.name ? `${t.name} <${t.address}>` : t.address).join(', ');
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this email? This action cannot be undone.')) {
            return;
        }

        try {
            setDeleting(true);
            // Use filename as emailId (it's the unique identifier)
            const emailId = encodeURIComponent(email.filename || email.id);
            const response = await axios.delete(
                `${BASEURL}superadmin/email/${userId}?emailId=${emailId}`,
                { headers: { token } }
            );

            if (response.data.success) {
                alert('Email deleted successfully!');
                if (onDelete) {
                    onDelete(email.filename || email.id);
                }
                onClose();
            } else {
                alert(response.data.error || 'Failed to delete email');
            }
        } catch (err) {
            console.error('Error deleting email:', err);
            alert(err.response?.data?.error || 'Failed to delete email');
        } finally {
            setDeleting(false);
        }
    };

    const handleReply = async () => {
        if (!replyData.from || !replyData.to || !replyData.subject || !replyData.text.trim()) {
            alert('Please fill in all required fields');
            return;
        }

        try {
            setSending(true);

            // Prepare reply data with references
            const replyPayload = {
                from: replyData.from,
                to: replyData.to,
                subject: replyData.subject,
                text: replyData.text,
                html: replyData.html || replyData.text.replace(/\n/g, '<br>'),
                inReplyTo: email.messageId,
                references: email.references && email.references.length > 0 
                    ? [...email.references, email.messageId].join(' ')
                    : email.messageId
            };

            const response = await axios.post(
                `${BASEURL}superadmin/email/reply/${userId}`,
                replyPayload,
                { headers: { token } }
            );

            if (response.data.success) {
                alert('Reply sent successfully!');
                setShowReply(false);
                setReplyData({
                    from: email.accountEmail || '',
                    to: email.from?.address || '',
                    subject: email.subject?.startsWith('Re:') ? email.subject : `Re: ${email.subject}`,
                    text: '',
                    html: ''
                });
            } else {
                alert(response.data.error || 'Failed to send reply');
            }
        } catch (err) {
            console.error('Error sending reply:', err);
            alert(err.response?.data?.error || 'Failed to send reply');
        } finally {
            setSending(false);
        }
    };

    return (
        <div className={styles.modalOverlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h2>Email Details</h2>
                    <button className={styles.closeButton} onClick={onClose}>&times;</button>
                </div>

                <div className={styles.modalContent}>
                    {!showReply ? (
                        <>
                            {/* Email Details */}
                            <div className={styles.emailDetails}>
                                <div className={styles.detailRow}>
                                    <strong>From:</strong>
                                    <span>{getFromDisplay(email.from)}</span>
                                </div>
                                <div className={styles.detailRow}>
                                    <strong>To:</strong>
                                    <span>{getToDisplay(email.to)}</span>
                                </div>
                                {email.cc && email.cc.length > 0 && (
                                    <div className={styles.detailRow}>
                                        <strong>CC:</strong>
                                        <span>{getToDisplay(email.cc)}</span>
                                    </div>
                                )}
                                <div className={styles.detailRow}>
                                    <strong>Subject:</strong>
                                    <span>{email.subject}</span>
                                </div>
                                <div className={styles.detailRow}>
                                    <strong>Date:</strong>
                                    <span>{formatDate(email.date)}</span>
                                </div>
                                <div className={styles.detailRow}>
                                    <strong>Account:</strong>
                                    <span>{email.accountEmail} ({email.accountDomain})</span>
                                </div>
                            </div>

                            {/* Email Body */}
                            <div className={styles.emailBody}>
                                {email.html ? (
                                    <div 
                                        className={styles.htmlContent}
                                        dangerouslySetInnerHTML={{ __html: email.html }}
                                    />
                                ) : (
                                    <div className={styles.textContent}>
                                        {email.text.split('\n').map((line, i) => (
                                            <p key={i}>{line}</p>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Attachments */}
                            {email.attachments && email.attachments.length > 0 && (
                                <div className={styles.attachments}>
                                    <strong>Attachments:</strong>
                                    <ul>
                                        {email.attachments.map((att, i) => (
                                            <li key={i}>
                                                {att.filename} ({att.size} bytes)
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Actions */}
                            <div className={styles.actions}>
                                <button 
                                    className={styles.replyButton}
                                    onClick={() => setShowReply(true)}
                                >
                                    Reply
                                </button>
                                <button 
                                    className={styles.deleteButton}
                                    onClick={handleDelete}
                                    disabled={deleting}
                                >
                                    {deleting ? 'Deleting...' : 'Delete'}
                                </button>
                                <button 
                                    className={styles.actionCloseButton}
                                    onClick={onClose}
                                >
                                    Close
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Reply Form */}
                            <div className={styles.replyForm}>
                                <h3>Reply to Email</h3>
                                
                                <div className={styles.formGroup}>
                                    <label>From:</label>
                                    <input
                                        type="email"
                                        value={replyData.from}
                                        onChange={(e) => setReplyData({ ...replyData, from: e.target.value })}
                                        required
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>To:</label>
                                    <input
                                        type="email"
                                        value={replyData.to}
                                        onChange={(e) => setReplyData({ ...replyData, to: e.target.value })}
                                        required
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>Subject:</label>
                                    <input
                                        type="text"
                                        value={replyData.subject}
                                        onChange={(e) => setReplyData({ ...replyData, subject: e.target.value })}
                                        required
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>Message:</label>
                                    <textarea
                                        value={replyData.text}
                                        onChange={(e) => setReplyData({ ...replyData, text: e.target.value })}
                                        rows="10"
                                        required
                                        placeholder="Type your reply here..."
                                        className={styles.replyTextarea}
                                    />
                                </div>

                                <div className={styles.formActions}>
                                    <button
                                        className={styles.sendButton}
                                        onClick={handleReply}
                                        disabled={sending}
                                    >
                                        {sending ? 'Sending...' : 'Send Reply'}
                                    </button>
                                    <button
                                        className={styles.cancelButton}
                                        onClick={() => {
                                            setShowReply(false);
                                            setReplyData({
                                                from: email.accountEmail || '',
                                                to: email.from?.address || '',
                                                subject: email.subject?.startsWith('Re:') ? email.subject : `Re: ${email.subject}`,
                                                text: '',
                                                html: ''
                                            });
                                        }}
                                        disabled={sending}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default EmailDetailModal;

